"use client";

import { useEffect, useRef, useState } from "react";
import { useFormStatus } from "react-dom";
import Image from "next/image";
import Link from "next/link";
import { useAuth } from "@/contexts/AuthContext";
import clsx from "clsx";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/shadcn/ui/sheet";
import Price from "@/components/shop/product/Price";
import { DEFAULT_OPTION } from "lib/constants";
import { createUrl } from "lib/utils";
import {
  createCartAndSetCookie,
  redirectToCheckout,
} from "@/lib/shopify/actions/cart";
import { useCart } from "@/contexts/CartContext";
import { DeleteItemButton } from "./delete-item-button";
import { EditItemQuantityButton } from "./edit-item-quantity-button";
import { ShoppingCart, X } from "lucide-react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import OpenCart from "./open-cart";
import AuthModal from "@/components/shop/auth/AuthModal";
import { CornerButton } from "@/shadcn/ui/corner-button";
import { debounce } from "lodash-es";
import placeholder from "@/public/shop/placeholder.svg";

type MerchandiseSearchParams = {
  [key: string]: string;
};

export default function CartModal({ type }: { type: "light" | "transparent" }) {
  const { cart, updateCartItem } = useCart();
  const { isAuthenticated, isLoading: isLoadingAuth } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const quantityRef = useRef(cart?.totalQuantity);

  useEffect(() => {
    if (!cart) {
      createCartAndSetCookie();
    }
  }, [cart]);

  useEffect(() => {
    if (
      cart?.totalQuantity &&
      cart?.totalQuantity !== quantityRef.current &&
      cart?.totalQuantity > 0
    ) {
      if (!isOpen) {
        setIsOpen(true);
      }
      quantityRef.current = cart?.totalQuantity;
    }
  }, [isOpen, cart?.totalQuantity, quantityRef]);

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <button
          aria-label="Warenkorb öffnen"
          onClick={() => setIsOpen(true)}
          className=""
        >
          <OpenCart type={type} quantity={cart?.totalQuantity} />
        </button>
      </SheetTrigger>
      <SheetContent className="w-full h-full md:w-[50rem] md:max-w-[50rem] bg-[#F3F2F1] [&_.close-btn]:hidden">
        <SheetHeader>
          <SheetTitle className="font-funnel font-[400]">Mein Warenkorb</SheetTitle>
          <VisuallyHidden>
            <SheetDescription></SheetDescription>
          </VisuallyHidden>

          <button
            aria-label="Warenkorb schließen"
            onClick={() => setIsOpen(false)}
            className="absolute top-[1rem] right-[1.5rem]"
          >
            <CloseCart />
          </button>
        </SheetHeader>
        {!cart || cart.lines.length === 0 ? (
          <div className="flex w-full h-full flex-col items-center justify-center overflow-hidden">
            <ShoppingCart className="h-20 w-20 text-black/80" />
            <p className="mt-6 text-center text-2xl font-[400] text-black/80 font-funnel">
              Ihr Warenkorb ist leer.
            </p>
          </div>
        ) : (
          <div className="flex h-[calc(100%-5rem)] flex-col justify-between overflow-hidden p-1">
            <ul className="grow overflow-clip py-4 text-black/70">
              {cart.lines
                .sort((a, b) =>
                  a.merchandise.product.title.localeCompare(
                    b.merchandise.product.title,
                  ),
                )
                .map((item, i) => {
                  const merchandiseSearchParams = {} as MerchandiseSearchParams;

                  item.merchandise.selectedOptions.forEach(
                    ({ name, value }) => {
                      if (value !== DEFAULT_OPTION) {
                        merchandiseSearchParams[name.toLowerCase()] = value;
                      }
                    },
                  );

                  const merchandiseUrl = createUrl(
                    `/shop/product/${item.merchandise.product.handle}`,
                    new URLSearchParams(merchandiseSearchParams),
                  );

                  return (
                    <li
                      key={i}
                      className="flex w-full flex-col border-b border-neutral-300 dark:border-neutral-700"
                    >
                      <div className="relative flex w-full flex-row gap-[.5rem] justify-between px-1 py-4">
                        <div className="absolute z-40 -ml-1 -mt-2">
                          <DeleteItemButton
                            item={item}
                            optimisticUpdate={updateCartItem}
                          />
                        </div>
                        <div className="flex flex-row">
                          <div className="relative h-[5rem] w-[5rem] min-h-[5rem] min-w-[5rem] overflow-hidden rounded-full border border-neutral-300 bg-neutral-300 dark:border-neutral-700 dark:bg-neutral-900 dark:hover:bg-neutral-800">
                            <Image
                              className="h-full w-full object-cover"
                              width={100}
                              height={100}
                              alt={
                                item.merchandise.product.featuredImage?.altText || item.merchandise.product.title
                              }
                              src={item.merchandise.product.featuredImage?.url || placeholder.src}
                            />
                          </div>
                          <Link
                            href={merchandiseUrl}
                            onClick={() => setIsOpen(false)}
                            className="z-30 ml-2 flex flex-row space-x-4 max-w-[30rem]"
                          >
                            <div className="flex flex-1 flex-col text-base">
                              <span className="leading-tight line-clamp-1 overflow-hidden overflow-ellipsis">
                                {item.merchandise.product.title}
                              </span>
                              {item.merchandise.title !== DEFAULT_OPTION ? (
                                <p className="text-sm text-neutral-400 dark:text-neutral-400">
                                  {item.merchandise.title}
                                </p>
                              ) : null}
                            </div>
                          </Link>
                        </div>
                        <div className="flex flex-col gap-[.25rem] items-center pe-[1rem] min-w-[10rem]">
                          <Price
                            className="flex justify-end text-right text-sm mb-0"
                            amount={item.cost.totalAmount.amount}
                            currencyCode={item.cost.totalAmount.currencyCode}
                          />
                          <div className="ml-auto flex min-h-[3rem] !h-[3rem] flex-row items-center rounded-full border border-neutral-200 dark:border-neutral-700">
                            <EditItemQuantityButton
                              item={item}
                              type="minus"
                              optimisticUpdate={debounce(updateCartItem, 100)}
                            />
                            <p className="w-6 text-center mb-0">
                              <span className="w-full text-sm">
                                {item.quantity}
                              </span>
                            </p>
                            <EditItemQuantityButton
                              item={item}
                              type="plus"
                              optimisticUpdate={debounce(updateCartItem, 100)}
                            />
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
            </ul>
            <div className="py-4 text-sm text-neutral-500 dark:text-neutral-400">
              <div className="mb-3 flex items-center justify-between border-b border-neutral-200 pb-1 dark:border-neutral-700">
                <p>Steuern</p>
                <Price
                  className="text-right text-base text-black dark:text-white"
                  amount={cart.cost.totalTaxAmount.amount}
                  currencyCode={cart.cost.totalTaxAmount.currencyCode}
                />
              </div>
              <div className="py-6 flex items-center justify-between border-b border-neutral-200 dark:border-neutral-700">
                <p className="mb-0">Versand</p>
                <p className="text-right mb-0">Wird an der Kasse berechnet</p>
              </div>
              <div className="mb-3 py-6 flex items-center justify-between border-b border-neutral-200 dark:border-neutral-700">
                <p className="mb-0">Gesamt</p>
                <Price
                  className="text-right text-base text-black dark:text-white mb-0"
                  amount={cart.cost.totalAmount.amount}
                  currencyCode={cart.cost.totalAmount.currencyCode}
                />
              </div>
            </div>
            <form action={redirectToCheckout}>
              <CheckoutButton
                isAuthenticated={isAuthenticated}
                isLoadingAuth={isLoadingAuth}
              />
            </form>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function CloseCart({ className }: { className?: string }) {
  return (
    <div className="relative flex items-center justify-center text-black/60">
      <X
        className={clsx(
          "h-14 w-14 transition-all ease-in-out hover:scale-110",
          className,
        )}
      />
    </div>
  );
}

function CheckoutButton({
  isAuthenticated,
  isLoadingAuth,
}: {
  isAuthenticated: boolean;
  isLoadingAuth: boolean;
}) {
  const { pending } = useFormStatus();

  if (!isLoadingAuth && !isAuthenticated) {
    return (
      <AuthModal theme={"light"} defaultMode="login">
        <CornerButton
          className="[clip-path:polygon(0_0,100%_0,100%_calc(100%-2rem),calc(100%-2rem)_100%,0_100%)] !bg-black w-full"
          type="dark"
          htmlType="button"
        >
          <div className="corner-button-text relative">
            <div className="corner-button-text-initial transition-all duration-300">
              Zur Kasse
            </div>
            <div className="corner-button-text-hover absolute top-[3rem] transition-all duration-300 pointer-events-none">
              Zur Kasse
            </div>
          </div>
        </CornerButton>
      </AuthModal>
    );
  }

  return (
    <CornerButton
      className="[clip-path:polygon(0_0,100%_0,100%_calc(100%-2rem),calc(100%-2rem)_100%,0_100%)] !bg-black w-full"
      type="dark"
      htmlType="submit"
      disabled={pending || isLoadingAuth}
    >
      <div className="corner-button-text relative">
        <div className="corner-button-text-initial transition-all duration-300">
          {pending || isLoadingAuth ? (
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white mx-auto"></div>
          ) : (
            "Zur Kasse"
          )}
        </div>
        <div className="corner-button-text-hover absolute top-[3rem] transition-all duration-300 pointer-events-none">
          {pending || isLoadingAuth ? (
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white mx-auto"></div>
          ) : (
            "Zur Kasse"
          )}
        </div>
      </div>
    </CornerButton>
  );
}
